import { cn } from '@/lib/utils'
import { formatKES } from '@/lib/auth'

interface StatItem {
  label: string
  value: string
  sub?: string
  variant?: 'default' | 'overdue' | 'success' | 'neutral'
}

export function ProfileStatCards({ stats, className }: { stats: StatItem[]; className?: string }) {
  return (
    <div className={cn('grid gap-4 sm:grid-cols-2 xl:grid-cols-4', className)}>
      {stats.map((stat) => (
        <div key={stat.label} className="rounded-card border border-border-2 bg-card px-5 py-4">
          <p className="font-sans text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
            {stat.label}
          </p>
          <p
            className={cn(
              'mt-2 font-serif text-stat-sm leading-none tracking-tight',
              stat.variant === 'overdue' && 'text-status-overdue-text',
              stat.variant === 'success' && 'text-status-paid-text',
              stat.variant === 'neutral' && 'text-muted-foreground',
              (!stat.variant || stat.variant === 'default') && 'text-ink',
            )}
          >
            {stat.value}
          </p>
          {stat.sub && <p className="mt-1.5 font-sans text-xs text-muted-foreground">{stat.sub}</p>}
        </div>
      ))}
    </div>
  )
}

export function buildProfileStats(profile: {
  outstandingBalance: number | null
  plots: { id: string }[]
  createdAt: number
  lastLoginAt: number | null
  role: string
}): StatItem[] {
  const owed = profile.outstandingBalance ?? 0
  const balance =
    owed > 0
      ? {
          label: 'Balance',
          value: `${formatKES(owed)} due`,
          sub: 'Unpaid bills',
          variant: 'overdue' as const,
        }
      : owed < 0
        ? {
            label: 'Balance',
            value: `${formatKES(-owed)} credit`,
            sub: 'Taken off their next bill',
            variant: 'success' as const,
          }
        : {
            label: 'Balance',
            value: formatKES(0),
            sub: 'All bills paid',
            variant: 'success' as const,
          }

  const stats: StatItem[] = [balance]

  if (profile.role !== 'admin' && profile.role !== 'estate_manager') {
    stats.push({
      label: 'Plots',
      value: String(profile.plots.length),
      sub: profile.plots.length === 1 ? 'Assigned plot' : 'Assigned plots',
      variant: 'default',
    })
  }

  stats.push(
    {
      label: 'Member since',
      value: new Date(profile.createdAt * 1000).toLocaleDateString('en-KE', {
        month: 'short',
        year: 'numeric',
      }),
      variant: 'neutral',
    },
    {
      label: 'Last sign-in',
      value: profile.lastLoginAt
        ? new Date(profile.lastLoginAt * 1000).toLocaleDateString('en-KE', {
            day: 'numeric',
            month: 'short',
          })
        : 'Never',
      variant: 'neutral',
    },
  )

  return stats
}
